import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { corCreate, corUpdate, crmListLeads } from '../../../api';
import { toast } from '../../../hooks/use-toast';
import { L, DIRECTION, STATUS, ENTITY_TYPE, CATEGORY, label, inputCls, Field } from './shared';

const empty = { direction: 'incoming', date: new Date().toISOString().slice(0, 10), entity: '', entity_type: 'ministry', subject: '', category: 'other', notes: '', status: 'new', due_at: '', lead_id: '' };

export const CorrespondenceForm = ({ open, onClose, lang, initial, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...empty, ...initial, due_at: initial.due_at || '', lead_id: initial.lead_id || '' } : empty);
    crmListLeads({ limit: 300 }).then((d) => setLeads(Array.isArray(d) ? d : d.leads || [])).catch(() => setLeads([]));
  }, [open, initial]);

  const submit = async () => {
    if (!form.entity.trim() || !form.subject.trim() || !form.date) {
      toast({ title: L(lang, 'Entity, subject and date are required', 'الجهة والموضوع والتاريخ مطلوبة'), variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, due_at: form.due_at || null, lead_id: form.lead_id || null };
      const saved = initial?.id ? await corUpdate(initial.id, payload) : await corCreate(payload);
      toast({ title: initial?.id ? L(lang, 'Saved', 'تم الحفظ') : L(lang, `Created ${saved.id}`, `تم الإنشاء ${saved.id}`) });
      onSaved(saved);
      onClose();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || L(lang, 'Failed', 'فشل الحفظ'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const opts = (map) => Object.keys(map).map((k) => <option key={k} value={k}>{label(map, k, lang)}</option>);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="cor-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{initial?.id ? L(lang, `Edit ${initial.id}`, `تعديل ${initial.id}`) : L(lang, 'New Correspondence', 'مخاطبة جديدة')}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <Field label={L(lang, 'Direction', 'النوع')}>
            <div className="flex gap-2">
              {Object.keys(DIRECTION).map((d) => (
                <button key={d} type="button" onClick={() => set('direction', d)} data-testid={`cor-dir-${d}`} className={`flex-1 h-9 rounded-md border text-sm transition-colors ${form.direction === d ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-white border-[#e5e7eb] text-[#111827]/70 hover:border-[#2563eb]/40'}`}>{label(DIRECTION, d, lang)}</button>
              ))}
            </div>
          </Field>
          <Field label={L(lang, 'Letter date', 'تاريخ المخاطبة')}><input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className={inputCls} data-testid="cor-date" /></Field>
          <Field label={L(lang, 'Entity (ministry / bank / free zone…)', 'الجهة (وزارة / بنك / منطقة حرة…)')}><input value={form.entity} onChange={(e) => set('entity', e.target.value)} className={inputCls} data-testid="cor-entity" placeholder={L(lang, 'e.g. Ministry of Economy', 'مثال: وزارة الاقتصاد')} /></Field>
          <Field label={L(lang, 'Entity type', 'نوع الجهة')}><select value={form.entity_type} onChange={(e) => set('entity_type', e.target.value)} className={inputCls} data-testid="cor-entity-type">{opts(ENTITY_TYPE)}</select></Field>
          <Field label={L(lang, 'Subject', 'الموضوع')} className="sm:col-span-2"><input value={form.subject} onChange={(e) => set('subject', e.target.value)} className={inputCls} data-testid="cor-subject" /></Field>
          <Field label={L(lang, 'Category', 'التصنيف')}><select value={form.category} onChange={(e) => set('category', e.target.value)} className={inputCls} data-testid="cor-category">{opts(CATEGORY)}</select></Field>
          <Field label={L(lang, 'Status', 'الحالة')}><select value={form.status} onChange={(e) => set('status', e.target.value)} className={inputCls} data-testid="cor-status">{opts(STATUS)}</select></Field>
          <Field label={L(lang, 'Reply due date (optional)', 'تاريخ استحقاق الرد (اختياري)')}><input type="date" value={form.due_at} onChange={(e) => set('due_at', e.target.value)} className={inputCls} data-testid="cor-due" /></Field>
          <Field label={L(lang, 'Link to CRM lead (optional)', 'ربط بعميل CRM (اختياري)')}>
            <select value={form.lead_id} onChange={(e) => set('lead_id', e.target.value)} className={inputCls} data-testid="cor-lead">
              <option value="">—</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.name || l.company} · {l.id}</option>)}
            </select>
          </Field>
          <Field label={L(lang, 'Notes', 'ملاحظات')} className="sm:col-span-2"><textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} className="w-full px-3 py-2 rounded-md bg-white border border-[#e5e7eb] text-sm" data-testid="cor-notes" /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={onClose} className="ghost-button bg-transparent h-10 px-4 text-sm rounded-md">{L(lang, 'Cancel', 'إلغاء')}</Button>
          <Button onClick={submit} disabled={saving} className="gold-button h-10 px-6 text-sm rounded-md" data-testid="cor-save-btn">{saving ? '...' : L(lang, 'Save', 'حفظ')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
